import settings from '@/settings'

type Params = Record<string, string | string[]>

const BASE_URL = settings.apiUrl

const httpRegex = /^https?:\/\//


export default {
  fetch (resource: string, options: RequestInit = {}, params: Params = {}) {
    let queryString = getQueryString(params)
    // Prepend the ? character if the queryString is not empty
    queryString = queryString && ('?' + queryString)

    // Construct the url
    let url = `${resource}${queryString}`
    if (!httpRegex.test(resource))
      url = `${BASE_URL}/${url}`

    return fetch(url,
      mergeOptions(
        {
          credentials: 'include',
          headers: {
            'X-Requested-With': 'x',
            Accept: 'application/json',
          },
          mode: 'cors',
        },
        // Override any defaults with given options
        options,
      )
    )
  },

  json (
    resource: string,
    method: 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    data: any = {},
    options: RequestInit = {},
    params?: Params
  ) {
    const body = JSON.stringify(data)
    return this.fetch(resource,
      mergeOptions(
        options,
        {
          body,
          headers: {
            'Content-Type': 'application/json',
          },
          method
        },
      ),
      params
    )
  },

  // Shortcut methods for standard CRUD operations

  get (resource: string, options: RequestInit = {}, params?: Params) {
    return this.fetch(resource, { ...options, method: 'GET' }, params)
      .then(handleJSONResponse)
  },

  post (resource: string, data?: any, options?: RequestInit, params?: Params) {
    return this.json(resource, 'POST', data, options, params)
      .then(handleJSONResponse)
  },

  put (resource: string, data?: any, options?: RequestInit, params?: Params) {
    return this.json(resource, 'PUT', data, options, params)
      .then(handleJSONResponse)
  },

  patch (resource: string, data?: any, options?: RequestInit, params?: Params) {
    return this.json(resource, 'PATCH', data, options, params)
      .then(handleJSONResponse)
  },

  // delete should be a 204 and return no content
  delete (resource: string, data?: any, options?: RequestInit, params?: Params) {
    return this.json(resource, 'DELETE', data, options, params)
  },

  /**
   * Upload a blob.
   */
  blob (
    resource: string,
    name: string,
    value: Blob | Blob[],
    data: Record<string, string> = {},
    options: RequestInit = {}
  ) {
    // Create form object to store blob
    const formData = new FormData();

    // Append the form
    (Array.isArray(value) ? value : [value])
      .forEach(v => formData.append(name, v))

    // TODO: assert that `name` is not in `data`

    // Set additional data
    for (const key of Object.keys(data)) {
      formData.append(key, data[key])
    }

    return this.fetch(resource,
      mergeOptions(
        options,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
          },
          body: formData,
        },
      )
    ).then(handleJSONResponse)
  },

  // Blob file shortcut helpers

  /**
   * Upload a single File object.
   */
  file (
    resource: string,
    file: File,
    data?: Record<string, string>,
    options?: RequestInit
  ) {
    return this.blob(resource, 'file', file, data, options)
  },

  /**
   * Upload a FileList object.
   */
  files (
    resource: string,
    files: FileList,
    data?: Record<string, string>,
    options?: RequestInit
  ) {
    return this.blob(resource, 'files', Array.from(files), data, options)
  },
}

/**
 * Handles JSON Responses from the back-end.
 *
 * Ref: https://css-tricks.com/using-fetch/
 *
 * @param response the fetch response object
 */
function handleJSONResponse (response: Response): Promise<any> {
  // check for 204 response
  if (response.status === 204) {
    return Promise.resolve()
  }
  return response.json()
    .then(json => {
      if (response.ok) {
        return json
      } else {
        return Promise.reject(Object.assign({}, json, {
          status: response.status,
          statusText: response.statusText,
        }))
      }
    })
}

function getQueryString (params: Params) {
  return Object.entries(params).map(([key, value]) => {
    if (Array.isArray(value)) {
      return value.map(v => encodeParam(key, v)).filter(s => s).join('&')
    }
    return encodeParam(key, value)
  }).filter(s => s).join('&')
}

function encodeParam (key: string, value: string) {
  return value && `${key}=${encodeURIComponent(value)}`
}

function mergeOptions (...options: RequestInit[]): RequestInit {
  // Merge all request headers together
  const headers = mergeHeaders(...options.map(o => o.headers || {}))
  return Object.assign({}, ...options, { headers })
}

function mergeHeaders (...headers: HeadersInit[]): HeadersInit {
  return Object.assign({}, ...headers)
}
